import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import BackButton from '../components/BackButton';

const BG = '#FDF5E6';
const INK = '#111';
const CABINET_BG = '#14131A';
const CREAM = '#FFF6E2';
const VALUE = '#FDE3CE';
const PINK = '#F58FB0';
const BLUE = '#8FD3F4';
const GREEN = '#84FAB0';
const FONT_BODY = "'K2D', sans-serif";
const FONT_DISPLAY = "'Bayon', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

const EMAIL = 'akim.cajp@gmail.com';
const MAILTO = `mailto:${EMAIL}?subject=Hello%20from%20your%20site`;

const ITEMS = [
  {
    label: 'EMAIL',
    meta: 'FASTEST',
    href: MAILTO,
    target: '_self',
    value: EMAIL,
    blurb: 'Straight to my inbox where I read every message from artificial and real people. Bonus points if you sound like a real person!',
  },
  {
    label: 'LINKEDIN',
    meta: 'CONNECT',
    href: 'https://www.linkedin.com/in/aki-kim',
    target: '_blank',
    value: 'linkedin.com/in/aki-kim',
    blurb: 'My work relationship history one pager. No swiping but you can always send me a connect request  with a note;)',
  },
  {
    label: 'RESUME',
    meta: 'ON REQUEST',
    href: `mailto:${EMAIL}?subject=Resume%20request`,
    target: '_self',
    value: EMAIL,
    blurb: 'Ask and I will send it over as a PDF, with context on whichever role you have in mind.',
  },
  {
    label: 'COFFEE',
    meta: 'TORONTO',
    href: `mailto:${EMAIL}?subject=Coffee%3F`,
    target: '_self',
    value: EMAIL,
    blurb: "Toronto, Ontario. Let's ditch social media and go old school over a cup of coffee. Or tie your shoe laces and let's go for a run!",
  },
];

const SCORE = '1,048,576';

const ContactPage = () => {
  const [sel, setSel] = useState(0);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setSel((s) => (s + (e.key === 'ArrowDown' ? 1 : ITEMS.length - 1)) % ITEMS.length);
        setCopied(false);
      } else if (e.key === 'Enter') {
        activate(ITEMS[sel]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(copyTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel]);

  const activate = (item) => {
    if (item.target === '_blank') window.open(item.href, '_blank', 'noopener,noreferrer');
    else window.location.href = item.href;
  };

  const selectItem = (i) => {
    setSel(i);
    setCopied(false);
  };

  const copyValue = () => {
    const item = ITEMS[sel];
    if (navigator.clipboard) navigator.clipboard.writeText(item.value).catch(() => {});
    setCopied(true);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1800);
  };

  const active = ITEMS[sel];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      style={{
        minHeight: '100vh',
        backgroundColor: BG,
        backgroundImage:
          'linear-gradient(rgba(0,0,0,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,.045) 1px, transparent 1px)',
        backgroundSize: '28px 28px',
        fontFamily: FONT_BODY,
        color: INK,
      }}
    >
      <style>{`
        @keyframes arBlink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
        @keyframes arSoftBlink { 0%, 100% { opacity: .5; } 50% { opacity: 1; } }
        @keyframes arFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
        @keyframes arScan { from { transform: translateY(0); } to { transform: translateY(6px); } }
        .ar-info-link:hover { color: #fff; }
        .ar-copy-btn:hover { background: rgba(255,246,226,.26); }
      `}</style>

      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '26px 20px 48px', display: 'flex', flexDirection: 'column', gap: 26 }}>

        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <BackButton color="rgba(0,0,0,.55)" hoverColor="#000" fontFamily={FONT_BODY} />
          <div style={{ font: `500 10.5px ${FONT_MONO}`, letterSpacing: '.16em', color: 'rgba(0,0,0,.4)' }}>
            CONTACT · 1 CREDIT REMAINING
          </div>
        </div>

        {/* Arcade cabinet */}
        <div
          style={{
            position: 'relative',
            borderRadius: 20,
            overflow: 'hidden',
            background: CABINET_BG,
            border: '1px solid rgba(0,0,0,.4)',
            boxShadow: '0 26px 50px -32px rgba(0,0,0,.75), 0 0 0 6px rgba(0,0,0,.05)',
          }}
        >
          {/* CRT scanlines */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 5,
              background: 'repeating-linear-gradient(180deg, rgba(255,255,255,.055) 0 1px, transparent 1px 3px)',
              animation: 'arScan 1.6s linear infinite',
            }}
          />
          {/* Vignette */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 6,
              background: 'radial-gradient(115% 95% at 50% 45%, transparent 55%, rgba(0,0,0,.55) 100%)',
            }}
          />

          <div style={{ position: 'relative', zIndex: 4, padding: '26px 30px 30px', display: 'flex', flexDirection: 'column', gap: 22, minHeight: 620 }}>

            {/* Score row */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, font: `700 10px ${FONT_MONO}`, letterSpacing: '.2em' }}>
              <div style={{ color: PINK }}>
                <div>1UP</div>
                <div style={{ color: VALUE, marginTop: 6 }}>{SCORE}</div>
              </div>
              <div style={{ textAlign: 'center', color: BLUE }}>
                <div>HI-SCORE</div>
                <div style={{ color: VALUE, marginTop: 6 }}>1,000,000</div>
              </div>
              <div style={{ textAlign: 'right', color: GREEN }}>
                <div>STAGE</div>
                <div style={{ color: VALUE, marginTop: 6 }}>CONTACT</div>
              </div>
            </div>

            {/* Headline */}
            <div style={{ textAlign: 'center', marginTop: 6 }}>
              <div style={{ font: `700 11px ${FONT_MONO}`, letterSpacing: '.28em', color: GREEN, animation: 'arSoftBlink 1.6s ease-in-out infinite' }}>
                PLAYER 2 HAS ENTERED THE GAME
              </div>
              <div
                style={{
                  font: `400 clamp(52px, 7.2vw, 104px)/.86 ${FONT_DISPLAY}`,
                  letterSpacing: '.02em',
                  color: CREAM,
                  marginTop: 14,
                  textShadow: '0 0 26px rgba(196,113,245,.55)',
                }}
              >
                Get in touch
              </div>
              <div style={{ font: `400 13.5px/1.55 ${FONT_BODY}`, color: 'rgba(255,246,226,.6)', maxWidth: '40ch', margin: '14px auto 0', textWrap: 'pretty' }}>
                Pick a row with the arrow keys or a click — every option on the board ends with you reaching me somewhere real.
              </div>
            </div>

            {/* Item list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, margin: '8px auto 0', width: '100%', maxWidth: 470 }}>
              {ITEMS.map((item, i) => {
                const on = i === sel;
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    target={item.target}
                    rel="noopener noreferrer"
                    onMouseEnter={() => selectItem(i)}
                    onClick={(e) => {
                      e.preventDefault();
                      activate(item);
                    }}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '38px 1fr auto',
                      alignItems: 'center',
                      gap: 12,
                      padding: '13px 16px',
                      borderRadius: 12,
                      background: on ? 'rgba(255,255,255,.09)' : 'transparent',
                      border: on ? '1px solid rgba(255,246,226,.35)' : '1px solid transparent',
                      textDecoration: 'none',
                      transition: 'background .18s ease, border-color .18s ease',
                    }}
                  >
                    {on ? (
                      <img
                        src="/ghost.png"
                        alt=""
                        style={{ width: 28, height: 28, objectFit: 'contain', display: 'block', imageRendering: 'pixelated', animation: 'arFloat 1.1s ease-in-out infinite' }}
                      />
                    ) : (
                      <span />
                    )}
                    <span style={{ font: `700 17px ${FONT_MONO}`, letterSpacing: '.14em', color: on ? CREAM : 'rgba(255,246,226,.42)' }}>
                      {item.label}
                    </span>
                    <span style={{ font: `400 12px ${FONT_MONO}`, letterSpacing: '.1em', color: on ? GREEN : 'rgba(132,250,176,.62)' }}>
                      {item.meta}
                    </span>
                  </a>
                );
              })}
            </div>

            {/* Info / detail row */}
            <div style={{ margin: '2px auto 0', width: '100%', maxWidth: 470, borderTop: '1px solid rgba(255,246,226,.14)', paddingTop: 16, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <span style={{ font: `700 10px ${FONT_MONO}`, letterSpacing: '.2em', color: BLUE, flex: 'none', paddingTop: 3 }}>INFO</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', font: `400 13.5px/1.55 ${FONT_BODY}`, color: 'rgba(255,246,226,.72)', textWrap: 'pretty' }}>
                  {active.blurb}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 11, flexWrap: 'wrap' }}>
                  <a
                    href={active.href}
                    target={active.target}
                    rel="noopener noreferrer"
                    className="ar-info-link"
                    style={{ font: `500 12.5px ${FONT_MONO}`, color: VALUE, borderBottom: '1px solid rgba(253,227,206,.45)', paddingBottom: 1, transition: 'color .18s ease' }}
                  >
                    {active.value}
                  </a>
                  <button
                    type="button"
                    onClick={copyValue}
                    className="ar-copy-btn"
                    style={{
                      appearance: 'none',
                      cursor: 'pointer',
                      font: `700 9px ${FONT_MONO}`,
                      letterSpacing: '.16em',
                      background: 'rgba(255,246,226,.14)',
                      color: CREAM,
                      border: '1px solid rgba(255,246,226,.3)',
                      borderRadius: 999,
                      padding: '5px 10px',
                      transition: 'background .18s ease',
                    }}
                  >
                    {copied ? 'COPIED' : 'COPY'}
                  </button>
                </span>
              </span>
            </div>

            <div style={{ flex: 1 }} />

            {/* Footer controls */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', font: `700 10px ${FONT_MONO}`, letterSpacing: '.2em', color: 'rgba(255,246,226,.45)' }}>
              <span>↑ ↓ SELECT · ENTER START</span>
              <span style={{ color: VALUE, animation: 'arBlink 1s steps(1,end) infinite' }}>INSERT COIN</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ContactPage;
